import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ChartBarIcon, SparklesIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'

function Analytics() {
  const { user } = useAuth()

  const sampleData = {
    monthlyCPP: [
      { month: 'Jan', cpp: 0.018, redemptions: 3 },
      { month: 'Feb', cpp: 0.022, redemptions: 2 },
      { month: 'Mar', cpp: 0.025, redemptions: 4 },
      { month: 'Apr', cpp: 0.020, redemptions: 1 },
      { month: 'May', cpp: 0.028, redemptions: 2 },
      { month: 'Jun', cpp: 0.030, redemptions: 3 }
    ],
    programPerformance: [
      { program: 'American Airlines', avgCPP: 0.025, redemptions: 5, totalValue: 1250 },
      { program: 'Marriott Bonvoy', avgCPP: 0.012, redemptions: 3, totalValue: 420 },
      { program: 'Delta SkyMiles', avgCPP: 0.022, redemptions: 4, totalValue: 880 },
      { program: 'Hilton Honors', avgCPP: 0.015, redemptions: 2, totalValue: 300 },
      { program: 'United MileagePlus', avgCPP: 0.020, redemptions: 1, totalValue: 200 }
    ],
    redemptionTypes: [
      { type: 'International Business', value: 35, color: '#3B82F6' },
      { type: 'Domestic Economy', value: 25, color: '#8B5CF6' },
      { type: 'Luxury Hotels', value: 20, color: '#10B981' },
      { type: 'Car Rentals', value: 15, color: '#F59E0B' },
      { type: 'Other', value: 5, color: '#EF4444' }
    ]
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">
            {user ? 'Deep insights into your loyalty program performance' : 'Explore sample analytics and see what insights await you'}
          </p>
        </div>
        
        {!user && (
          <Link to="/register" className="btn-primary flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5" />
            <span>Unlock Your Analytics</span>
          </Link>
        )}
      </div>

      {/* Guest User Banner */}
      {!user && (
        <div className="bg-gradient-to-r from-purple-50 to-primary-50 border border-purple-200 rounded-xl p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-primary-600 rounded-xl flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Unlock Personalized Analytics
              </h3>
              <p className="text-gray-600 mb-3">
                Get detailed insights into your CPP trends, program performance, and redemption patterns. Sign up to track your own data and see personalized analytics.
              </p>
              <div className="flex space-x-3">
                <Link to="/register" className="btn-primary">
                  Create Free Account
                </Link>
                <Link to="/chatbot" className="btn-secondary">
                  Ask AI for Insights
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CPP Trend Chart */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {user ? 'Your CPP Trend' : 'Sample CPP Trend'}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={sampleData.monthlyCPP}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip formatter={(value) => [`${(value * 100).toFixed(1)}¢`, 'CPP']} />
            <Line type="monotone" dataKey="cpp" stroke="#3B82F6" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
        {!user && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Sample data shown • Sign up to track your own CPP trends
          </p>
        )}
      </div>

      {/* Program Performance */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {user ? 'Program Performance' : 'Sample Program Performance'}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={sampleData.programPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="program" />
            <YAxis />
            <Tooltip formatter={(value) => [`${(value * 100).toFixed(1)}¢`, 'CPP']} />
            <Bar dataKey="avgCPP" fill="#8B5CF6" />
          </BarChart>
        </ResponsiveContainer>
        {!user && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Sample data shown • Sign up to analyze your own programs
          </p>
        )}
      </div>

      {/* Redemption Types Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {user ? 'Your Redemption Types' : 'Sample Redemption Types'}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sampleData.redemptionTypes}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {sampleData.redemptionTypes.map((entry, index) => (
                  <Cell key={'cell-${index}'} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-success-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ArrowTrendingUpIcon className="w-4 h-4 text-success-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Best Value</h4>
                <p className="text-sm text-gray-600">International business class flights provide the highest CPP at 3.0¢</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <ChartBarIcon className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Program Analysis</h4>
                <p className="text-sm text-gray-600">American Airlines consistently delivers above-average CPP</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-warning-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <SparklesIcon className="w-4 h-4 text-warning-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Optimization</h4>
                <p className="text-sm text-gray-600">Focus on premium travel and luxury hotels for maximum value</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action for Guests */}
      {!user && (
        <div className="card text-center py-8">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <ChartBarIcon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ready for Your Analytics?</h3>
          <p className="text-gray-500 mb-6">Sign up to track your own redemptions and unlock personalized insights</p>
          <div className="flex justify-center space-x-3">
            <Link to="/register" className="btn-primary">
              Create Free Account
            </Link>
            <Link to="/chatbot" className="btn-secondary">
              Ask AI for Advice
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default Analytics
